import useSWR from "swr";
import { getUserToken } from "src/utils/jwtToken";
import { useActingAsOverrideHeader } from "../auth";
import { useDedupeInterval } from "../configuration";
import { useTokenRefreshHandler } from "../hooks";
import { useUserService } from "../services";
import {
  Guest,
  GuestStatus,
  Host,
  HostAuthStatus,
  MeetingStatus,
} from "../types";
import { errorHandler } from "src/hooks/errorHandler";
import { CoHostFilterOption } from "../componentsV2/filters/CoHostFilter";

interface MeetingQueryV1 {
  filter: {
    query: string;
    categories: string[];
    guestStatusFilter: GuestStatus[];
    dateFilter?: {
      end?: string; // ex. 2023-03-24T23:59:59
      start?: string; // ex. 2023-03-24T23:59:59
      timezone?: string;
    };
    lastActivityFilter?: {
      end?: string; // ex. 2023-03-24T23:59:59
      start?: string; // ex. 2023-03-24T23:59:59
      timezone?: string;
    };
    tagFilter: number[];
    meetingTypeFilter: number[];
    meetingStatusFilter: MeetingStatus[];
  };
  sort: { field: "meetingTime" | "lastActivity"; order: "asc" | "desc" };
  paging: { limit: number; offset: number };
}

export interface MeetingQueryV2 {
  filter: {
    meetingIdFilter?: number[];
    guestEmail: string;
    hostEmail: string;
    notes: string;
    guestStatusFilter: GuestStatus[];
    dateFilter?: {
      end?: string; // ex. 2023-03-24T23:59:59
      start?: string; // ex. 2023-03-24T23:59:59
      timezone?: string;
    };
    lastActivityFilter?: {
      end?: string; // ex. 2023-03-24T23:59:59
      start?: string; // ex. 2023-03-24T23:59:59
      timezone?: string;
    };
    tagFilter: number[];
    meetingTypeFilter: number[];
    meetingStatusFilter: MeetingStatus[];
    coHostFilter: CoHostFilterOption | null;
    scheduledLater?: boolean;
    hostAuthInvalid?: boolean;
    calClog?: boolean;
  };
  sort: { field: "meetingTime" | "lastActivity"; order: "asc" | "desc" };
  paging: { limit: number; offset: number };
}

type MeetingQuery = MeetingQueryV1 | MeetingQueryV2;

export interface SkinnyMeeting {
  id: number;
  enabled: boolean;
  meetingType: {
    id: number;
    name: string;
    active: boolean;
  };
  attempts: {
    current: number;
    total: number;
  };
  guests: Guest[] | null;
  host: Host;
  hasCoHosts: boolean | null;
  startTime: string;
  status: MeetingStatus;
  executeAt?: string | null;
  hostAuthStatus: HostAuthStatus;
}

export function useMeetingSearchResultsNewPage(query: Partial<MeetingQuery>) {
  const accessToken = getUserToken();
  const service = useUserService();
  const tokenRefreshHandler = useTokenRefreshHandler();
  const override = useActingAsOverrideHeader();

  const headers: { [index: string]: string } = {
    "JWT-TOKEN": accessToken as string,
  };

  if (override) {
    headers.override = override;
  }

  const interval = useDedupeInterval();

  const { data, error } = useSWR(
    [`/api/meetings/instances/list`, query],
    (url: string, body: object) =>
      service
        .post(url)
        .set(headers)
        .send(body)
        .then(tokenRefreshHandler)
        .then((res: Response) => res.body)
        .catch(errorHandler),
    { dedupingInterval: interval },
  );

  return {
    data: data as { total: number; data: SkinnyMeeting[] } | undefined,
    error,
    loading: !error && !data,
  };
}
